import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { updateAppointment as updateAppointmentAction } from 'actions';
import { lookUpClientPaymentSetting as lookUpClientPaymentSettingAction } from 'actions/BookingActions';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { getAppointment } from 'selectors/appointment';

import { Contact } from 'components/BookingSteps/Contact/Contact';

import { useCancellation } from 'hooks/cancellation/useCancellation';
import { useAppContext } from 'hooks/context/useAppContext';

import AppointmentInfo from 'routes/book/AppointmentInfo';

import { TAppointmentContact } from 'types/appointment';

import { getClientFromCookie, setClientCookie } from 'utils/clientCookie';

import { Pricing } from '../Pricing/Pricing';

const CLOSED_BOOKING_SNIPPET = 'does not accept online bookings';

type TClientPaymentSettingsQueryParams = {
  firstName: string;
  lastName: string;
  pronouns: string;
  email: string;
  phone: string;
  slug: string;
};

type TProps = {
  navigateToNextStep: ({ stepKey }: { stepKey: string }) => void;
};

export const Contacts = ({ navigateToNextStep }: TProps) => {
  const [showingOfflineBookingModal, setShowingOfflineBookingModal] =
    React.useState(false);
  const [showingError, setShowingError] = React.useState(false);
  const { publicUser } = useAppContext();

  const appointment = useSelector(getAppointment) || {};

  const dispatch = useDispatch<ThunkDispatch<any, any, AnyAction>>();
  // Dispatch handlers
  const lookUpClientPaymentSetting = (
    data: TClientPaymentSettingsQueryParams,
    navigateToNextStep: TProps['navigateToNextStep'],
  ) => dispatch(lookUpClientPaymentSettingAction(data, navigateToNextStep));

  const updateAppointmentField = (name: string, value: string | boolean) =>
    dispatch(updateAppointmentAction(name, value));

  const { cancellationPolicyForceAccept } = useCancellation();
  const clientData = getClientFromCookie();

  React.useEffect(() => {
    if (appointment.error?.error?.includes(CLOSED_BOOKING_SNIPPET)) {
      setShowingOfflineBookingModal(true);
    }
  }, [appointment.error]);

  const onSubmit = (values: TAppointmentContact) => {
    const {
      pronouns,
      firstName,
      lastName,
      email,
      phone,
      cancellationPolicyAcknowledged,
      agreedToSmsConsent,
    } = values;

    const client = {
      ...clientData,
      pronouns: pronouns.trim(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      phone: phone.trim(),
    };

    setClientCookie(client);
    updateAppointmentField('firstName', client.firstName);
    updateAppointmentField('lastName', client.lastName);
    updateAppointmentField('pronouns', client.pronouns);
    updateAppointmentField('email', client.email);
    updateAppointmentField('phone', client.phone);
    updateAppointmentField(
      'cancellationPolicyAcknowledged',
      cancellationPolicyForceAccept && cancellationPolicyAcknowledged,
    );
    updateAppointmentField('agreedToSmsConsent', agreedToSmsConsent);

    return new Promise((resolve) => {
      lookUpClientPaymentSetting(
        {
          ...client,
          slug: publicUser.slug,
        },
        (params: { stepKey: string }) => navigateToNextStep(params),
      );
      resolve(true);
    });
  };

  React.useEffect(() => {
    if (showingError) {
      setTimeout(() => {
        setShowingError(false);
      }, 500);
    }
  }, [showingError]);

  const appointmentErrorMessage = appointment?.error?.error;
  const isLoading = appointment?.loading;

  const erorrMessage =
    appointmentErrorMessage &&
    !appointmentErrorMessage.includes(CLOSED_BOOKING_SNIPPET)
      ? appointmentErrorMessage
      : null;

  const defaultValues = {
    firstName: clientData.firstName,
    lastName: clientData.lastName,
    pronouns: clientData.pronouns,
    email: clientData.email,
    phone: clientData.phone,
    cancellationPolicyAcknowledged: false,
    agreedToSmsConsent: false,
  };

  return (
    <>
      <Contact
        defaultValues={defaultValues}
        errorMessage={erorrMessage}
        hideOfflineBookingModal={() => setShowingOfflineBookingModal(false)}
        isLoading={isLoading}
        isOfflineBookingModalVisible={showingOfflineBookingModal}
        onSubmit={onSubmit}
      />
      <AppointmentInfo />
      <Pricing />
    </>
  );
};
